// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
} from '@mui/material';
import { omitBy } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import { useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import { UserListHead } from '../sections/@dashboard/user';
import { Search, SearchIconWrapper, StyledInputBase } from '../components/SearchInput';

// store
import { api as stockApi } from '../store/domains/stock';
import { useDebounce } from '../utils/debounce';
import {
  ROWS_PER_PAGE_OPTIONS,
  SORT_OPTIONS,
  ENTITY_STATUS_OPTIONS,
  ENTITY_STATUS_ACTIVE,
  ENTITY_STATUS_DEACTIVE,
} from '../constants/dealer';

const TABLE_HEAD = [
  { id: 'id', label: 'ID', alignRight: false },
  { id: 'dealerCode', label: 'Dealer Code', alignRight: false },
  { id: 'phone', label: 'Phone', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'actions', label: '', alignRight: false },
];

const MySwal = withReactContent(Swal);

function resolveProviderId() {
  return localStorage.getItem('providerId') === '1' ? 'AZF' : 'BKC';
}

// Backend sends the entity status as a single char ('a' / 'd'); be tolerant of
// longer spellings ('ACTIVE' / 'DEACTIVE') in case the list endpoint differs.
function normalizeStatus(status) {
  const first = (status ?? '').toString().trim().toLowerCase().charAt(0);
  return first === ENTITY_STATUS_DEACTIVE ? ENTITY_STATUS_DEACTIVE : ENTITY_STATUS_ACTIVE;
}

function statusLabel(status) {
  return ENTITY_STATUS_OPTIONS.find((option) => option.value === normalizeStatus(status))?.label ?? '';
}

export default function DealerBind() {
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('asc');

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDealerId, setEditDealerId] = useState(null);
  const [editDealerCode, setEditDealerCode] = useState('');
  const [editDealerPhone, setEditDealerPhone] = useState('');
  const [editDealerStatus, setEditDealerStatus] = useState(ENTITY_STATUS_ACTIVE);
  const [togglingId, setTogglingId] = useState(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDealerCode, setNewDealerCode] = useState('');
  const [newDealerPhone, setNewDealerPhone] = useState('');

  const debouncedSearch = useDebounce(searchValue, 400);
  const providerId = resolveProviderId();

  const params = useMemo(
    () =>
      omitBy(
        {
          size: rowsPerPage,
          page,
          title: debouncedSearch,
          sort: sortBy,
          provider: providerId,
        },
        (v) => v == null || v.toString().length < 1
      ),
    [page, rowsPerPage, providerId, debouncedSearch, sortBy]
  );

  const { data: dealer, isLoading: dealerLoading, isFetching } = stockApi.useGetDealerPhone(params);

  const [editDealer, { isLoading: editLoading }] = stockApi.useEditDealerPhone();
  const [createDealer, { isLoading: createLoading }] = stockApi.useCreateDealerPhone();

  const handleChangeSearchInput = (event) => {
    setSearchValue(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEditDialog = (row) => {
    setEditDealerId(row.id);
    setEditDealerCode(row.dealer);
    setEditDealerPhone(row.phone);
    setEditDealerStatus(normalizeStatus(row.status));
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditDealerId(null);
    setEditDealerCode('');
    setEditDealerPhone('');
    setEditDealerStatus(ENTITY_STATUS_ACTIVE);
  };

  const handleEditSubmit = async () => {
    if (editDealerId == null) return;
    try {
      await editDealer({
        id: editDealerId,
        provider: providerId,
        dealerCode: editDealerCode,
        phoneNumber: editDealerPhone,
        entityStatus: editDealerStatus,
      }).unwrap();
      handleCloseEditDialog();
      MySwal.fire('Success', 'Dealer updated successfully', 'success');
    } catch {
      handleCloseEditDialog();
    }
  };

  const handleToggleStatus = async (row) => {
    const currentStatus = normalizeStatus(row.status);
    const nextStatus = currentStatus === ENTITY_STATUS_ACTIVE ? ENTITY_STATUS_DEACTIVE : ENTITY_STATUS_ACTIVE;
    const actionLabel = nextStatus === ENTITY_STATUS_ACTIVE ? 'activate' : 'deactivate';

    const { isConfirmed } = await MySwal.fire({
      title: `Are you sure you want to ${actionLabel} ${row.phone}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    });
    if (!isConfirmed) return;

    setTogglingId(row.id);
    try {
      await editDealer({
        id: row.id,
        provider: providerId,
        dealerCode: row.dealer,
        phoneNumber: row.phone,
        entityStatus: nextStatus,
      }).unwrap();
      MySwal.fire('Success', `Number ${actionLabel}d successfully`, 'success');
    } catch {
      // the axios response interceptor already surfaces the backend message
    } finally {
      setTogglingId(null);
    }
  };

  const handleOpenCreateDialog = () => setCreateDialogOpen(true);

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewDealerCode('');
    setNewDealerPhone('');
  };

  const handleCreateSubmit = async () => {
    if (!newDealerCode || !newDealerPhone) return;
    try {
      await createDealer({
        provider: providerId,
        dealerCode: newDealerCode,
        phoneNumber: newDealerPhone,
      }).unwrap();
      handleCloseCreateDialog();
      MySwal.fire('Success', 'Dealer created successfully', 'success');
    } catch {
      handleCloseCreateDialog();
    }
  };

  if (dealerLoading) {
    return <CircularProgress />;
  }

  const rows = dealer?.content ?? [];
  const totalCount = dealer?.totalElements ?? 0;

  return (
    <Page>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              onChange={handleChangeSearchInput}
              value={searchValue}
              placeholder="Search…"
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search>

          <Button onClick={handleOpenCreateDialog} sx={{ width: '200px', maxHeight: '56px' }} variant="contained">
            Add New Dealer
          </Button>

          <FormControl>
            <InputLabel id="sort-by-label">Sort By</InputLabel>
            <Select labelId="sort-by-label" label="Sort By" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Card>
          {isFetching && <LinearProgress />}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead headLabel={TABLE_HEAD} rowCount={rows.length} />
                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((row) => {
                      const { id, dealer: dealerCode, phone, status } = row;
                      const isActive = normalizeStatus(status) === ENTITY_STATUS_ACTIVE;
                      return (
                        <TableRow hover key={id ?? dealerCode} tabIndex={-1}>
                          <TableCell padding="checkbox" />
                          <TableCell align="left">{id}</TableCell>
                          <TableCell align="left">{dealerCode}</TableCell>
                          <TableCell align="left">{phone}</TableCell>
                          <TableCell align="left">
                            <Chip
                              size="small"
                              label={statusLabel(status)}
                              color={isActive ? 'success' : 'default'}
                              variant={isActive ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Button
                                size="small"
                                color={isActive ? 'error' : 'success'}
                                onClick={() => handleToggleStatus(row)}
                                disabled={editLoading}
                              >
                                {togglingId === id ? '…' : (isActive && 'Deactivate') || 'Activate'}
                              </Button>
                              <Button size="small" onClick={() => handleOpenEditDialog(row)} disabled={editLoading}>
                                Edit
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={TABLE_HEAD.length + 1} align="center">
                        No results
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Edit Dealer Details</DialogTitle>
          <DialogContent>
            <Stack spacing={3} mt={1}>
              <TextField
                label="Dealer Code"
                variant="outlined"
                size="small"
                fullWidth
                value={editDealerCode}
                disabled
              />
              <TextField
                label="Dealer Phone"
                variant="outlined"
                size="small"
                fullWidth
                value={editDealerPhone}
                onChange={(e) => setEditDealerPhone(e.target.value)}
              />
              <FormControl fullWidth size="small">
                <InputLabel id="edit-status-label">Status</InputLabel>
                <Select
                  labelId="edit-status-label"
                  label="Status"
                  value={editDealerStatus}
                  onChange={(e) => setEditDealerStatus(e.target.value)}
                >
                  {ENTITY_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={editLoading}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} variant="contained" disabled={editLoading}>
              {editLoading ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Create New Dealer</DialogTitle>
          <DialogContent>
            <Stack spacing={3} mt={1}>
              <TextField
                label="Dealer Code"
                variant="outlined"
                size="small"
                fullWidth
                value={newDealerCode}
                onChange={(e) => setNewDealerCode(e.target.value)}
              />
              <TextField
                label="Dealer Phone"
                variant="outlined"
                size="small"
                fullWidth
                value={newDealerPhone}
                onChange={(e) => setNewDealerPhone(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog} disabled={createLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              variant="contained"
              disabled={createLoading || !newDealerCode || !newDealerPhone}
            >
              {createLoading ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
